const FUNNEL_STAGE_RANK = {
  identificado: 10,
  contactado: 20,
  calificado: 30,
  propuesta: 40,
  negociacion: 50,
  negociación: 50,
  cierre: 60,
  perdido: 70,
};

export const FUNNEL_ELIMINADO_LABEL = 'Eliminado';
export const FUNNEL_IDENTIFICADO_LABEL = 'Identificado';
export const FUNNEL_PROPUESTA_LABEL = 'Propuesta';
export const FUNNEL_NEGOCIACION_LABEL = 'Negociación';
export const FUNNEL_CIERRE_LABEL = 'Cierre';

const normalizeKey = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const funnelStageRank = (stage = '') => {
  const key = normalizeKey(stage);
  return Object.prototype.hasOwnProperty.call(FUNNEL_STAGE_RANK, key) ? FUNNEL_STAGE_RANK[key] : -1;
};

export const isEliminadoFunnelStage = (stage = '') => normalizeKey(stage) === 'eliminado';

const normalizeQuotationStatus = (value = '') => {
  const key = normalizeKey(value);
  if (key === 'enviada') return 'Emitida';
  if (key === 'aprobada') return 'Adjudicada';
  if (key === 'rechazada') return 'Rechazada';
  return String(value || '').trim();
};

/** Piso de etapa según cotizaciones no-borrador (alineado al backend). */
export const quotationFunnelFloor = (quotations = []) => {
  const statuses = (Array.isArray(quotations) ? quotations : [])
    .map((row) => normalizeQuotationStatus(row?.status || row))
    .filter((status) => status && normalizeKey(status) !== 'borrador');
  if (!statuses.length) return '';
  if (statuses.some((status) => normalizeKey(status) === 'vendida')) return FUNNEL_CIERRE_LABEL;
  if (statuses.some((status) => normalizeKey(status) === 'adjudicada')) return FUNNEL_NEGOCIACION_LABEL;
  return FUNNEL_PROPUESTA_LABEL;
};

export const applyFunnelFloor = (stage = '', floor = '') => {
  const current = String(stage || '').trim();
  const floorStage = String(floor || '').trim();
  if (!floorStage) return current || FUNNEL_IDENTIFICADO_LABEL;
  if (!current) return floorStage;
  if (funnelStageRank(floorStage) > funnelStageRank(current)) return floorStage;
  return current;
};

export const resolveContactFunnelStage = (stages = []) => {
  const normalized = (Array.isArray(stages) ? stages : [])
    .map((stage) => String(stage || '').trim())
    .filter(Boolean);
  if (!normalized.length) return '';
  if (normalized.some((stage) => isEliminadoFunnelStage(stage))) return FUNNEL_ELIMINADO_LABEL;
  const ranked = normalized
    .map((stage) => ({ stage, rank: funnelStageRank(stage) }))
    .filter((row) => row.rank >= 0);
  if (!ranked.length) return normalized[0];
  return ranked.sort((a, b) => b.rank - a.rank)[0].stage;
};

export const resolveOrganizationFunnelStage = (contactStages = []) => {
  const eligible = (Array.isArray(contactStages) ? contactStages : [])
    .map((stage) => String(stage || '').trim())
    .filter((stage) => stage && !isEliminadoFunnelStage(stage));
  if (!eligible.length) return FUNNEL_IDENTIFICADO_LABEL;
  return resolveContactFunnelStage(eligible);
};

export const pipelineFunnelStagesFromOptions = (options = []) =>
  (Array.isArray(options) ? options : [])
    .map((opt) => String(opt.label || opt.value || '').trim())
    .filter((label) => label && !isEliminadoFunnelStage(label));

export const funnelStagePresentation = (stageRaw = '', isDarkMode = false) => {
  const label = String(stageRaw || '').trim() || '—';
  const key = normalizeKey(label);

  const lightPalette = {
    identificado: { bg: '#f3f4f6', color: '#374151', border: '#9ca3af' },
    contactado: { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
    calificado: { bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' },
    propuesta: { bg: '#ede9fe', color: '#6d28d9', border: '#c4b5fd' },
    negociacion: { bg: '#f3e8ff', color: '#7e22ce', border: '#d8b4fe' },
    cierre: { bg: '#dcfce7', color: '#166534', border: '#86efac' },
    perdido: { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
    eliminado: { bg: '#f4f4f5', color: '#52525b', border: '#d4d4d8' },
  };

  const darkPalette = {
    identificado: { bg: '#3f3f46', color: '#e4e4e7', border: '#71717a' },
    contactado: { bg: '#1e3a8a', color: '#dbeafe', border: '#2563eb' },
    calificado: { bg: '#0c4a6e', color: '#e0f2fe', border: '#0ea5e9' },
    propuesta: { bg: '#4c1d95', color: '#ede9fe', border: '#8b5cf6' },
    negociacion: { bg: '#581c87', color: '#f3e8ff', border: '#a855f7' },
    cierre: { bg: '#14532d', color: '#dcfce7', border: '#22c55e' },
    perdido: { bg: '#7f1d1d', color: '#fee2e2', border: '#ef4444' },
    eliminado: { bg: '#27272a', color: '#d4d4d8', border: '#52525b' },
  };

  const palette = isDarkMode ? darkPalette : lightPalette;
  const fallback = isDarkMode
    ? { bg: '#27272a', color: '#e4e4e7', border: '#3f3f46' }
    : { bg: '#f0ebe3', color: '#44403c', border: '#d6d0c4' };

  const tone = palette[key] || fallback;
  return {
    label,
    bg: tone.bg,
    color: tone.color,
    border: tone.border,
  };
};
